import { createExponentialBackoff } from '@nzyme/utils/createExponentialBackoff.js';
import { createSingleRunner } from '@nzyme/utils/createSingleRunner.js';
import { maxOptional } from '@nzyme/utils/maxOptional.js';
import { onEventEmitter } from '@nzyme/vue-utils/onEventEmitter.js';
import { reactive } from '@nzyme/vue-utils/reactivity/reactive.js';
import debounce from 'lodash.debounce';
import { computed, ref } from 'vue';
import { ChatActor } from '@sigma/api-core/actors/ChatActor.js';
const PAGE_SIZE = 10;
/**
 * Composable for managing a paginated chat list with real-time updates.
 * Each call creates a new independent instance, suitable for contexts
 * where multiple chat lists may coexist (e.g. doctor views).
 *
 * @__NO_SIDE_EFFECTS__
 */
export function useChatList(options) {
    const chatRef = options.actorClient.actor(ChatActor);
    const chats = ref();
    const pending = ref(false);
    let loading;
    const lastItem = computed(() => {
        return chats.value?.[chats.value.length - 1];
    });
    const hasMore = computed(() => {
        return lastItem.value?.hasMore ?? false;
    });
    // Queue of read mark requests awaiting flush, keyed by chatId with the highest seq.
    const readMarkQueue = new Map();
    // Shared debounced trigger for flushing read marks.
    const debouncedFlush = debounce(() => void readMarkRunner.execute(), 300);
    // Single runner ensures only one flush runs at a time; requests that arrive
    // during a flush are picked up by the next execution.
    const readMarkRunner = createSingleRunner({ handler: flushReadMarks });
    // Exponential backoff for read mark flush retries (1s → 2s → 4s → ... → 30s cap).
    const readMarkBackoff = createExponentialBackoff({ baseDelay: 1000, maxDelay: 30_000 });
    // Generation counter incremented on reset() so in-flight flushReadMarks can detect
    // that a reset occurred and bail out instead of scheduling a stale retry.
    let readMarkGeneration = 0;
    onEventEmitter(chatRef.events.chatEvents, ({ event }) => handleChatEvents(event));
    return reactive({
        chats: chats,
        hasMore,
        pending,
        reload,
        loadMore,
        refresh,
        pushChat,
        markRead,
        getReadSeq,
        reset,
    });
    async function reload() {
        if (chats.value) {
            return;
        }
        await loadPageInternal();
    }
    async function loadMore() {
        if (loading) {
            return;
        }
        const last = lastItem.value;
        if (!last?.hasMore) {
            return;
        }
        try {
            pending.value = true;
            await loadPageInternal(last.timestamp);
        }
        finally {
            pending.value = false;
        }
    }
    /**
     * Fetches the first page of chats and merges them into the existing list.
     * Covers gaps that real-time events may have missed (e.g. app was backgrounded),
     * without discarding already-loaded lower pages.
     */
    async function refresh() {
        if (!chats.value) {
            return;
        }
        const result = await options.loadPage({ count: PAGE_SIZE });
        const map = new Map();
        for (const item of chats.value) {
            map.set(item.id, item);
        }
        for (const item of result) {
            map.set(item.id, item);
        }
        chats.value = Array.from(map.values());
        sortChats(chats.value);
    }
    async function loadPageInternal(beforeTimestamp) {
        const promise = doLoadPage(beforeTimestamp);
        loading = promise;
        try {
            await promise;
        }
        finally {
            if (loading === promise) {
                loading = undefined;
            }
        }
    }
    async function doLoadPage(beforeTimestamp) {
        const result = await options.loadPage({ beforeTimestamp, count: PAGE_SIZE });
        // Deduplicate chats
        const map = new Map();
        if (chats.value) {
            for (const item of chats.value) {
                // Reset stale hasMore on existing items. Real-time events can move
                // the last chat to the top and leave a previously non-last chat as
                // the new last with hasMore=true — an infinite loadMore loop if the
                // backend has no older rows. Backend-provided hasMore from `result`
                // for overlapping items still wins via the Map override below.
                // Build a new object rather than mutating the reactive item so the
                // hasMore computed doesn't fire on an intermediate state — only the
                // chats.value reassignment below triggers a single atomic recompute.
                map.set(item.id, { ...item, hasMore: false });
            }
        }
        for (const item of result) {
            map.set(item.id, item);
        }
        const newChats = Array.from(map.values());
        sortChats(newChats);
        chats.value = newChats;
    }
    function pushChat(item) {
        if (!chats.value) {
            return;
        }
        const filtered = chats.value.filter(c => c.id !== item.id);
        chats.value = [item, ...filtered];
    }
    /**
     * Queues a read mark for the given chat and debounces the API sync.
     * Local state and UI are updated only after the API call succeeds.
     */
    function markRead(chatId, seq) {
        const chat = chats.value?.find(c => c.id === chatId);
        if (!chat) {
            return;
        }
        // Only queue if seq is higher than what is already read or queued
        const currentSeq = chat.eventSeqRead;
        const queuedSeq = readMarkQueue.get(chatId);
        const effectiveSeq = Math.max(currentSeq ?? -1, queuedSeq ?? -1);
        if (seq <= effectiveSeq) {
            return;
        }
        readMarkQueue.set(chatId, seq);
        debouncedFlush();
    }
    /**
     * Returns the effective read seq: the max of the chat's synced `eventSeqRead`
     * and any read mark still queued for flush. Careful not to leak a `-1` default —
     * when only one source exists, that value is returned as-is.
     */
    function getReadSeq(chatId) {
        const chat = chats.value?.find(c => c.id === chatId);
        const syncedSeq = chat?.eventSeqRead;
        const queuedSeq = readMarkQueue.get(chatId);
        return maxOptional(syncedSeq, queuedSeq);
    }
    /**
     * Flushes all queued read marks to the API and updates local state on success.
     * Removes each entry from the queue only after a successful API call,
     * so that failed items remain queued for the next runner execution.
     *
     * Does NOT recurse. If items remain after the batch due to failures,
     * a retry is scheduled via exponential backoff through the runner, avoiding
     * unbounded loops when the API is persistently down. If remaining items are
     * only new arrivals queued during the flush, the next flush is scheduled
     * immediately without backoff to avoid unnecessary latency.
     */
    async function flushReadMarks() {
        const generation = readMarkGeneration;
        const batch = new Map(readMarkQueue);
        let anySucceeded = false;
        let anyFailed = false;
        for (const [chatId, seq] of batch) {
            try {
                await options.syncReadMark(chatId, seq);
            }
            catch (error) {
                // Log so persistent sync failures can be investigated; without this the
                // caller has no trace of the chatId / seq / error that kept retrying.
                options.logger?.error('Failed to sync read mark', { chatId, seq, error });
                // Keep the failed item in the queue so it is retried on the next flush.
                anyFailed = true;
                continue;
            }
            anySucceeded = true;
            // Remove from queue only after successful sync.
            // If a newer seq was queued for this chat during the API call, keep the newer value.
            if (readMarkQueue.get(chatId) === seq) {
                readMarkQueue.delete(chatId);
            }
            // Update local chat list state after API success
            const chat = chats.value?.find(c => c.id === chatId);
            if (chat) {
                if (chat.eventSeqRead == null || seq > chat.eventSeqRead) {
                    chat.eventSeqRead = seq;
                }
            }
        }
        if (readMarkQueue.size === 0) {
            readMarkBackoff.reset();
            return;
        }
        // Some items made progress — reset backoff so new failures start fresh.
        if (anySucceeded) {
            readMarkBackoff.reset();
        }
        // If no items failed, the remaining queue entries are new arrivals that were
        // queued during the flush. Schedule the next flush immediately without backoff
        // to avoid unnecessary latency for mark-as-read updates.
        if (!anyFailed) {
            setTimeout(() => {
                // If reset() was called between scheduling and firing, abandon this
                // retry — the new context will start its own flush.
                if (generation !== readMarkGeneration) {
                    return;
                }
                void readMarkRunner.execute();
            }, 0);
            return;
        }
        // Wait with exponential backoff (1s → 2s → 4s → ... → 30s cap with jitter),
        // then schedule a new runner execution. setTimeout(0) ensures the runner's
        // finally block has cleared state.promise before we re-trigger.
        await readMarkBackoff.backoff();
        // If reset() was called during the backoff wait, abandon this retry cycle.
        // The new context will start its own flush with a fresh backoff.
        if (generation !== readMarkGeneration) {
            return;
        }
        setTimeout(() => {
            // Re-check after the setTimeout fires: reset() may have been called
            // between the guard above and this callback.
            if (generation !== readMarkGeneration) {
                return;
            }
            void readMarkRunner.execute();
        }, 0);
    }
    function reset() {
        chats.value = undefined;
        loading = undefined;
        debouncedFlush.cancel();
        readMarkQueue.clear();
        readMarkBackoff.reset();
        readMarkGeneration++;
        // Clear the runner's promise so new markRead calls can start a fresh flush
        // immediately instead of deduplicating into the stale in-flight execution.
        // The abandoned execution's generation check will prevent it from scheduling retries.
        readMarkRunner.reset();
    }
    /**
     * Applies real-time events to the chat list.
     * Uses seq-based gap detection: if the fold returns null, we missed events
     * and fall back to reloading the chat from the API.
     */
    function handleChatEvents(events) {
        if (!chats.value) {
            return;
        }
        const reloadChatIds = new Set();
        for (const event of events) {
            // Already scheduled for reload — skip remaining events for this chat
            if (reloadChatIds.has(event.chatId)) {
                continue;
            }
            const chat = chats.value.find(c => c.id === event.chatId);
            if (!chat) {
                // Chat not in the list yet (new chat or not loaded)
                reloadChatIds.add(event.chatId);
                continue;
            }
            const newItem = options.foldState(chat, event);
            if (!newItem) {
                // Gap detected — local state is stale, need fresh data from API
                reloadChatIds.add(event.chatId);
                continue;
            }
            // Update item in place
            const index = chats.value.indexOf(chat);
            if (index >= 0) {
                chats.value[index] = newItem;
            }
            // Reset so it won't break pagination
            newItem.hasMore = true;
        }
        sortChats(chats.value);
        for (const chatId of reloadChatIds) {
            void reloadSingleChat(chatId);
        }
    }
    /** Fetches a single chat's current state from the API and upserts it into the list. */
    async function reloadSingleChat(chatId) {
        const result = await options.reloadChat(chatId);
        if (!result || !chats.value) {
            return;
        }
        const filtered = chats.value.filter(c => c.id !== chatId);
        chats.value = [result, ...filtered];
        sortChats(chats.value);
    }
    function sortChats(items) {
        items.sort((a, b) => b.timestamp - a.timestamp);
    }
}
//# sourceMappingURL=useChatList.js.map